import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';
import TextEditor from '../components/TextEditor';

function AdminDiseases() {
  const [diseases, setDiseases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [letterFilter, setLetterFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDisease, setEditingDisease] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    alias: '',
    first_letter: '',
    description: '',
    overview: '',
    symptoms: '',
    causes: '',
    risk_factors: '',
    complications: '',
    prevention: '',
    diagnosis_treatment: '',
    content: '',
    icd11_uri: '',
    status: 'active'
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [icdUriLookupLoading, setIcdUriLookupLoading] = useState(false);
  const [icdUriHint, setIcdUriHint] = useState('');
  const [icdImportQuery, setIcdImportQuery] = useState('');
  const [icdImportSearchLimit, setIcdImportSearchLimit] = useState(25);
  const [icdImportLetter, setIcdImportLetter] = useState('A');
  const [icdImportLetterLimit, setIcdImportLetterLimit] = useState(50);
  const [icdImportStatus, setIcdImportStatus] = useState('inactive');
  const [icdImportLoading, setIcdImportLoading] = useState(false);
  const [icdImportMessage, setIcdImportMessage] = useState('');
  const lastIcdUriLookupRef = useRef('');
  const navigate = useNavigate();

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  /** Normalize a pasted ICD-11 entity URI into a consistent `uri` string. */
  function normalizeIcdUriInput(raw) {
    let s = String(raw ?? '').trim();
    if (!s) return '';

    // If user pastes our own URL tail like `icd11-http%3A%2F...`
    if (s.startsWith('icd11-')) {
      s = s.slice('icd11-'.length);
    }

    // Decode once if it looks URL-encoded
    if (s.includes('%3A') || s.includes('%2F') || s.includes('%2f')) {
      try {
        s = decodeURIComponent(s);
      } catch (_) {
        /* ignore */
      }
    }

    // Support pasting just the host/path without scheme
    if (s.startsWith('id.who.int/')) {
      s = `https://${s}`;
    }

    // Support relative paths (rare, but safe)
    if (s.startsWith('/')) {
      s = `https://id.who.int${s}`;
    }

    return s;
  }

  const lookupDiseaseByIcdUri = useCallback(async (rawUri) => {
    const uri = normalizeIcdUriInput(rawUri);
    if (!uri) {
      setIcdUriHint('');
      return;
    }
    if (uri === lastIcdUriLookupRef.current) {
      return;
    }

    setIcdUriLookupLoading(true);
    setIcdUriHint('');
    try {
      const { data } = await axios.get('/api/diseases/icd/entity', {
        params: { uri, linearization: 'mms' }
      });

      lastIcdUriLookupRef.current = uri;
      const resolvedName =
        data?.name ||
        (typeof data?.title === 'string' ? data.title : data?.title?.['@value']) ||
        '';

      const toStr = (v) => (v == null ? '' : String(v));

      setFormData((prev) => {
        const has = (v) => v != null && String(v).trim() !== '';
        const nextName = resolvedName || prev.name;
        const nextFirstLetter = resolvedName
          ? resolvedName.charAt(0).toUpperCase()
          : prev.first_letter;

        return {
          ...prev,
          icd11_uri: uri,
          name: nextName,
          first_letter: nextFirstLetter,
          // User expectation: pasting URI should populate Name and Overview.
          overview: toStr(data.overview || data.description),
          description: has(prev.description) ? prev.description : toStr(data.description),
          symptoms: has(prev.symptoms) ? prev.symptoms : toStr(data.symptoms),
          causes: has(prev.causes) ? prev.causes : toStr(data.causes),
          risk_factors: has(prev.risk_factors) ? prev.risk_factors : toStr(data.risk_factors),
          complications: has(prev.complications) ? prev.complications : toStr(data.complications),
          prevention: has(prev.prevention) ? prev.prevention : toStr(data.prevention),
          diagnosis_treatment: has(prev.diagnosis_treatment) ? prev.diagnosis_treatment : toStr(data.diagnosis_treatment)
        };
      });

      setIcdUriHint(resolvedName ? `Loaded title from ICD-11: ${resolvedName}` : 'Loaded from ICD-11.');
    } catch (err) {
      lastIcdUriLookupRef.current = '';
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error || err.response?.data?.hint;
      if (status === 404) {
        setIcdUriHint(serverMsg || 'ICD-11 entity not found for this URI.');
      } else if (status === 503) {
        setIcdUriHint(serverMsg || 'ICD-11 API is not configured or unavailable.');
      } else {
        setIcdUriHint(serverMsg || err.message || 'Could not load ICD-11 data. Try again later.');
      }
    } finally {
      setIcdUriLookupLoading(false);
    }
  }, []);

  const icdUriDebounceRef = useRef(null);
  /** Debounced lookup for `icd11_uri` (paste often does not blur). */
  useEffect(() => {
    if (!showAddModal && !showEditModal) return undefined;

    const normalized = normalizeIcdUriInput(formData.icd11_uri);
    if (!normalized) return undefined;

    // If normalization changes the value, update it once and wait for the next effect run.
    if (normalized !== formData.icd11_uri) {
      setFormData((prev) => ({ ...prev, icd11_uri: normalized }));
      return undefined;
    }

    if (icdUriDebounceRef.current) clearTimeout(icdUriDebounceRef.current);
    icdUriDebounceRef.current = setTimeout(() => {
      icdUriDebounceRef.current = null;
      lookupDiseaseByIcdUri(normalized);
    }, 700);

    return () => {
      if (icdUriDebounceRef.current) clearTimeout(icdUriDebounceRef.current);
    };
  }, [formData.icd11_uri, formData.name, showAddModal, showEditModal, lookupDiseaseByIcdUri]);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchDiseases();
  }, [navigate, search, letterFilter, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, letterFilter]);

  const fetchDiseases = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (letterFilter) params.append('letter', letterFilter);
      params.append('include_inactive', 'true'); // Include inactive diseases for admin
      params.append('page', String(currentPage));

      const response = await axios.get(`/api/diseases?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setDiseases(response.data.diseases);
      setPagination(response.data.pagination);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error('Error fetching diseases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    lastIcdUriLookupRef.current = '';
    setIcdUriHint('');
    setFormData({
      name: '',
      alias: '',
      first_letter: '',
      description: '',
      overview: '',
      symptoms: '',
      causes: '',
      risk_factors: '',
      complications: '',
      prevention: '',
      diagnosis_treatment: '',
      content: '',
      icd11_uri: '',
      status: 'active'
    });
    setFormError('');
    setShowAddModal(true);
  };

  const handleEdit = async (disease) => {
    try {
      let fullDisease = disease;
      if (disease.id) {
        const token = getAccessToken();
        try {
          const response = await axios.get(`/api/diseases/${disease.id}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          fullDisease = response.data;
        } catch (error) {
          console.error('Error fetching full disease data:', error);
        }
      }

      setEditingDisease(fullDisease);
      lastIcdUriLookupRef.current = normalizeIcdUriInput(fullDisease.icd11_uri || '');
      setIcdUriHint('');
      setFormData({
        name: fullDisease.name || '',
        alias: fullDisease.alias || '',
        first_letter: fullDisease.first_letter || '',
        description: fullDisease.description || '',
        overview: fullDisease.overview || '',
        symptoms: fullDisease.symptoms || '',
        causes: fullDisease.causes || '',
        risk_factors: fullDisease.risk_factors || '',
        complications: fullDisease.complications || '',
        prevention: fullDisease.prevention || '',
        diagnosis_treatment: fullDisease.diagnosis_treatment || '',
        content: fullDisease.content || '',
        icd11_uri: fullDisease.icd11_uri || '',
        status: fullDisease.status || 'active'
      });
      setFormError('');
      setShowEditModal(true);
    } catch (error) {
      console.error('Error in handleEdit:', error);
      // Fallback to using the disease data we have
      setEditingDisease(disease);
      setFormData({
        name: disease.name || '',
        alias: disease.alias || '',
        first_letter: disease.first_letter || '',
        description: disease.description || '',
        overview: disease.overview || '',
        symptoms: disease.symptoms || '',
        causes: disease.causes || '',
        risk_factors: disease.risk_factors || '',
        complications: disease.complications || '',
        prevention: disease.prevention || '',
        diagnosis_treatment: disease.diagnosis_treatment || '',
        content: disease.content || '',
        status: disease.status || 'active'
      });
      setFormError('');
      setShowEditModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      const token = getAccessToken();
      const data = {
        ...formData,
        first_letter: formData.first_letter || formData.name.charAt(0).toUpperCase()
      };

      if (editingDisease) {
        await axios.put(`/api/diseases/${editingDisease.id}`, data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Disease updated successfully!');
      } else {
        await axios.post('/api/diseases', data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Disease created successfully!');
      }

      setShowAddModal(false);
      setShowEditModal(false);
      setEditingDisease(null);
      await fetchDiseases();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to save disease';
      setFormError(errorMessage);
      
      // If it's an authentication error, redirect to login
      if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        navigate('/admin/login');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this disease?')) return;

    try {
      const token = getAccessToken();
      await axios.delete(`/api/diseases/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      alert('Disease deleted successfully!');
      fetchDiseases();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete disease');
    }
  };

  const runIcdImportSearch = async () => {
    const q = String(icdImportQuery || '').trim();
    if (q.length < 2) {
      setIcdImportMessage('Enter at least 2 characters for ICD-11 search.');
      return;
    }
    setIcdImportLoading(true);
    setIcdImportMessage('');
    try {
      const token = getAccessToken();
      const { data } = await axios.post(
        '/api/diseases/icd/import-search',
        { q, limit: icdImportSearchLimit, status: icdImportStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIcdImportMessage(
        `Import finished: ${data.imported} new rows saved, ${data.skipped} skipped (already in DB or no URI), ${data.failed} WHO fetch failures. New diseases use status “${data.status_default}”.`
      );
      await fetchDiseases();
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/admin/login');
        return;
      }
      setIcdImportMessage(err.response?.data?.error || err.message || 'Import failed.');
    } finally {
      setIcdImportLoading(false);
    }
  };

  const runIcdImportLetter = async () => {
    const L = String(icdImportLetter || 'A').trim().toUpperCase();
    if (!/^[A-Z]$/.test(L)) {
      setIcdImportMessage('Choose a single letter A–Z for by-letter import.');
      return;
    }
    if (
      !window.confirm(
        `This will fetch up to ${icdImportLetterLimit} ICD-11 titles starting with “${L}” and save each as a new disease (skipping URIs already in the database). Continue?`
      )
    ) {
      return;
    }
    setIcdImportLoading(true);
    setIcdImportMessage('');
    try {
      const token = getAccessToken();
      const { data } = await axios.post(
        '/api/diseases/icd/import-letter',
        { letter: L, limit: icdImportLetterLimit, status: icdImportStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIcdImportMessage(
        `Letter “${L}” import: ${data.imported} new, ${data.skipped} skipped, ${data.failed} failed. Status “${data.status_default}”.`
      );
      await fetchDiseases();
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/admin/login');
        return;
      }
      setIcdImportMessage(err.response?.data?.error || err.message || 'Import failed.');
    } finally {
      setIcdImportLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="row mb-4">
          <div className="col">
            <h1 className="h3 mb-0">Diseases & Conditions Management</h1>
          </div>
          <div className="col-auto">
            <button className="btn btn-primary" onClick={handleAdd}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Disease
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder="Search diseases..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={letterFilter}
              onChange={(e) => setLetterFilter(e.target.value)}
            >
              <option value="">All Letters</option>
              {alphabet.map(letter => (
                <option key={letter} value={letter}>{letter}</option>
              ))}
              <option value="#">#</option>
            </select>
          </div>
        </div>

        {/* Bulk import ICD-11 → database (admin only; inactive by default for curation) */}
        <div className="card mb-4 border-primary-subtle">
          <div className="card-header bg-light">
            <strong>Import from ICD-11 into database</strong>
            <span className="text-muted small ms-2">
              Saves WHO entities as diseases you can edit (symptoms, causes, etc.). Skips URIs already stored.
            </span>
          </div>
          <div className="card-body">
            <div className="row g-3 align-items-end mb-3">
              <div className="col-md-3">
                <label className="form-label small mb-0">Default status for new rows</label>
                <select
                  className="form-select form-select-sm"
                  value={icdImportStatus}
                  onChange={(e) => setIcdImportStatus(e.target.value)}
                  disabled={icdImportLoading}
                >
                  <option value="inactive">Inactive (not on public site until you activate)</option>
                  <option value="active">Active (visible on public site immediately)</option>
                </select>
              </div>
            </div>
            <div className="row g-3 align-items-end">
              <div className="col-md-5">
                <label className="form-label">Search term (same as WHO search)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. diabetes"
                  value={icdImportQuery}
                  onChange={(e) => setIcdImportQuery(e.target.value)}
                  disabled={icdImportLoading}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Max results</label>
                <select
                  className="form-select"
                  value={icdImportSearchLimit}
                  onChange={(e) => setIcdImportSearchLimit(Number(e.target.value))}
                  disabled={icdImportLoading}
                >
                  {[10, 25, 50].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-auto">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={icdImportLoading}
                  onClick={runIcdImportSearch}
                >
                  {icdImportLoading ? (
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                  ) : null}
                  Import search results
                </button>
              </div>
            </div>
            <hr />
            <div className="row g-3 align-items-end">
              <div className="col-md-2">
                <label className="form-label">By first letter</label>
                <select
                  className="form-select"
                  value={icdImportLetter}
                  onChange={(e) => setIcdImportLetter(e.target.value)}
                  disabled={icdImportLoading}
                >
                  {alphabet.map((ltr) => (
                    <option key={ltr} value={ltr}>
                      {ltr}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Max entities</label>
                <select
                  className="form-select"
                  value={icdImportLetterLimit}
                  onChange={(e) => setIcdImportLetterLimit(Number(e.target.value))}
                  disabled={icdImportLoading}
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-auto">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={icdImportLoading}
                  onClick={runIcdImportLetter}
                >
                  Import letter batch
                </button>
              </div>
            </div>
            {icdImportMessage && (
              <p className="small text-muted mt-3 mb-0" role="status">
                {icdImportMessage}
              </p>
            )}
          </div>
        </div>

        {/* Diseases Table */}
        <div className="card">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
            <>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Alias</th>
                    <th>First Letter</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {diseases.map((disease) => (
                    <tr key={disease.id}>
                      <td>{disease.name}</td>
                      <td>{disease.alias || '-'}</td>
                      <td>{disease.first_letter}</td>
                      <td>
                        <span className={`badge bg-${disease.status === 'active' ? 'success' : 'secondary'}`}>
                          {disease.status}
                        </span>
                      </td>
                      <td>{new Date(disease.created_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary me-2"
                          onClick={() => handleEdit(disease)}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(disease.id)}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.pages > 1 && (
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  Page {pagination.page} of {pagination.pages} (Total: {pagination.total})
                </p>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={loading || (pagination.page || 1) <= 1}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={loading || (pagination.page || 1) >= (pagination.pages || 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingDisease ? 'Edit Disease' : 'Add New Disease'}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setEditingDisease(null);
                    setFormError('');
                  }}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {formError && (
                    <div className="alert alert-danger" role="alert">
                      {formError}
                    </div>
                  )}

                  <div className="mb-3">
                    <label htmlFor="icd11_uri" className="form-label">
                      ICD-11 entity URI (optional)
                    </label>
                    <div className="position-relative">
                      <input
                        type="url"
                        className="form-control"
                        id="icd11_uri"
                        value={formData.icd11_uri}
                        onChange={(e) => setFormData({ ...formData, icd11_uri: e.target.value })}
                        placeholder="https://id.who.int/..."
                      />
                      {icdUriLookupLoading && (
                        <span
                          className="position-absolute end-0 top-50 translate-middle-y pe-3 small text-muted"
                          style={{ pointerEvents: 'none' }}
                        >
                          Loading…
                        </span>
                      )}
                    </div>
                    {icdUriHint && (
                      <div
                        className={`form-text ${
                          icdUriHint.toLowerCase().includes('not found') ? 'text-warning' : ''
                        }`}
                      >
                        {icdUriHint}
                      </div>
                    )}
                    <div className="form-text">
                      Only needed if you are not using an ICD-11 code above. Paste the URL from the WHO browser or from
                      your site’s address bar (decoded <code>id.who.int/...</code> part).
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="alias" className="form-label">
                      Alias (Alternative Name)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="alias"
                      value={formData.alias}
                      onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                      placeholder="e.g., A fib for Atrial fibrillation"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="first_letter" className="form-label">
                      First Letter
                    </label>
                    <select
                      className="form-select"
                      id="first_letter"
                      value={formData.first_letter}
                      onChange={(e) => setFormData({ ...formData, first_letter: e.target.value })}
                    >
                      <option value="">Auto (from name)</option>
                      {alphabet.map(letter => (
                        <option key={letter} value={letter}>{letter}</option>
                      ))}
                      <option value="#">#</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">
                      Description (Short summary)
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      rows="3"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      readOnly
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="Overview"
                      value={formData.overview}
                      onChange={(val) => setFormData({ ...formData, overview: val })}
                      placeholder="Detailed overview of the disease. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={5}
                      readOnly
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="Symptoms"
                      value={formData.symptoms}
                      onChange={(val) => setFormData({ ...formData, symptoms: val })}
                      placeholder="Describe symptoms. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={6}
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="Causes"
                      value={formData.causes}
                      onChange={(val) => setFormData({ ...formData, causes: val })}
                      placeholder="Describe causes. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={6}
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="Risk Factors"
                      value={formData.risk_factors}
                      onChange={(val) => setFormData({ ...formData, risk_factors: val })}
                      placeholder="Describe risk factors. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={5}
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="Complications"
                      value={formData.complications}
                      onChange={(val) => setFormData({ ...formData, complications: val })}
                      placeholder="Describe complications. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={5}
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="Prevention"
                      value={formData.prevention}
                      onChange={(val) => setFormData({ ...formData, prevention: val })}
                      placeholder="Describe prevention methods. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={4}
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="Diagnosis & Treatment"
                      value={formData.diagnosis_treatment}
                      onChange={(val) => setFormData({ ...formData, diagnosis_treatment: val })}
                      placeholder="Describe diagnosis and treatment. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={6}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="content" className="form-label">
                      Additional Content (HTML - Optional)
                    </label>
                    <textarea
                      className="form-control"
                      id="content"
                      rows="4"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Additional content in HTML format (optional)"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="status" className="form-label">
                      Status
                    </label>
                    <select
                      className="form-select"
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setEditingDisease(null);
                      setFormError('');
                    }}
                    disabled={formLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={formLoading}
                  >
                    {formLoading ? 'Saving...' : editingDisease ? 'Update Disease' : 'Create Disease'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminDiseases;
